import type { AbuseSignalRecord, AdminConfigRecord, AdminOpsSummary, RunReplayResult } from '../api/types';
import type { AppState } from '../state/store';
import { btn, esc, pill, row } from './components';

const WARNING = 'Admin changes affect economy tuning. Invalid values are rejected server-side.';

function tokenPreview(token: string): string {
  return token.length > 8 ? `${token.slice(0, 4)}...${token.slice(-4)}` : 'saved';
}

function jsonText(value: unknown): string {
  return JSON.stringify(value, null, 2) ?? 'null';
}

function shortLabel(value: string | null): string {
  if (!value) return '—';
  return value.length > 10 ? `${value.slice(0, 10)}...` : value;
}

function textareaRows(value: unknown): number {
  const lines = jsonText(value).split('\n').length;
  return Math.max(3, Math.min(14, lines + 1));
}

function grouped(config: AdminConfigRecord[]): Array<[string, AdminConfigRecord[]]> {
  const groups = new Map<string, AdminConfigRecord[]>();
  for (const record of config) {
    groups.set(record.group, [...(groups.get(record.group) ?? []), record]);
  }
  return [...groups.entries()];
}

function lockedState(): string {
  return `
    <section class="card card--wide admin-head">
      <div class="run__head">
        <h2>Admin config</h2>
        ${btn('Back to Waystation', 'goto-waystation', {}, { variant: 'ghost' })}
      </div>
      <p class="banner banner--notice">${esc(WARNING)}</p>
      <div class="admin-token-row">
        <input class="market-price admin-token-input" type="password" data-admin-token placeholder="ADMIN_API_TOKEN" aria-label="ADMIN_API_TOKEN" />
        ${btn('Load config', 'admin-token-save', {}, { variant: 'primary' })}
      </div>
    </section>
    <section class="card card--wide admin-panel">
      <h2>Ops / Monitoring</h2>
      <p class="muted">Enter an admin token to load ops summary.</p>
    </section>`;
}

function configRecord(record: AdminConfigRecord): string {
  return `
    <article class="admin-config-row">
      <div class="admin-config-row__head">
        <div>
          <h3><code>${esc(record.key)}</code></h3>
          <p class="muted">Updated by ${esc(record.updatedBy ?? 'default')} ${record.updatedAt ? `· <code>${esc(record.updatedAt)}</code>` : ''}</p>
        </div>
        ${btn('Save', 'admin-save', { key: record.key }, { variant: 'primary' })}
      </div>
      <textarea class="admin-json" data-admin-config-key="${esc(record.key)}" rows="${textareaRows(record.value)}" aria-label="JSON value for ${esc(record.key)}">${esc(jsonText(record.value))}</textarea>
    </article>`;
}

function configList(config: AdminConfigRecord[]): string {
  return grouped(config)
    .map(([group, records]) => `
      <section class="admin-group">
        <h2>${esc(group)}</h2>
        <div class="admin-config-list">${records.map(configRecord).join('')}</div>
      </section>`)
    .join('');
}

function statusPill(status: AbuseSignalRecord['status']): string {
  if (status === 'open') return pill('open', 'warn');
  if (status === 'flagged') return pill('flagged', 'down');
  if (status === 'reviewed') return pill('reviewed', 'ok');
  return pill('dismissed', 'muted');
}

function severityPill(severity: AbuseSignalRecord['severity']): string {
  if (severity === 'high') return pill('high', 'down');
  if (severity === 'medium') return pill('medium', 'warn');
  return pill('low', 'muted');
}

function abuseRow(signal: AbuseSignalRecord): string {
  return `
    <tr>
      <td><code>${esc(signal.signalType)}</code></td>
      <td>${severityPill(signal.severity)}</td>
      <td>${statusPill(signal.status)}</td>
      <td><code>${esc(shortLabel(signal.accountId))}</code></td>
      <td><code>${esc(signal.createdAt)}</code></td>
      <td>${btn('Open', 'admin-abuse-open', { id: String(signal.id) }, { variant: 'ghost' })}</td>
    </tr>`;
}

function abuseDetail(signal: AbuseSignalRecord | null): string {
  if (!signal) return '<p class="muted">Select a signal to inspect payload and review state.</p>';
  return `
    <div class="admin-detail">
      <div class="admin-detail__head">
        <div>
          <h3><code>${esc(signal.signalType)}</code> #${esc(signal.id)}</h3>
          <p class="muted">Severity ${esc(signal.severity)} · status ${esc(signal.status)} · account <code>${esc(shortLabel(signal.accountId))}</code></p>
        </div>
        <div class="btn-row">
          ${btn('Review', 'admin-abuse-review', { id: String(signal.id) }, { variant: 'primary' })}
          ${btn('Dismiss', 'admin-abuse-dismiss', { id: String(signal.id) }, { variant: 'ghost' })}
          ${btn('Flag', 'admin-abuse-flag', { id: String(signal.id) }, { variant: 'danger' })}
        </div>
      </div>
      <pre class="admin-pre">${esc(jsonText({ payload: signal.payload, walletCluster: signal.walletCluster }))}</pre>
    </div>`;
}

function abuseSection(state: AppState): string {
  const signals = state.adminAbuseSignals ?? [];
  return `
    <section class="card card--wide admin-panel">
      <div class="run__head">
        <h2>Abuse Review</h2>
        <div class="btn-row">
          ${btn('Scan', 'admin-abuse-scan', {}, { variant: 'primary' })}
          ${btn('Refresh', 'admin-abuse-refresh', {}, { variant: 'ghost' })}
        </div>
      </div>
      <div class="admin-abuse-grid">
        <div class="admin-table-wrap">
          <table class="admin-table">
            <thead>
              <tr><th>Type</th><th>Severity</th><th>Status</th><th>Account</th><th>Created</th><th></th></tr>
            </thead>
            <tbody>
              ${signals.length > 0 ? signals.map(abuseRow).join('') : '<tr><td colspan="6" class="muted">No abuse signals loaded.</td></tr>'}
            </tbody>
          </table>
        </div>
        ${abuseDetail(state.adminSelectedAbuseSignal)}
      </div>
    </section>`;
}

function verifierPill(mode: string): string {
  if (mode === 'mock') return pill('mock', 'warn');
  if (mode === 'rpc') return pill('rpc', 'muted');
  return pill(mode, 'muted');
}

function opsWarnings(summary: AdminOpsSummary): string {
  if (summary.warnings.length === 0) return '<p class="muted">No active warnings.</p>';
  return `<ul>${summary.warnings.map((warning) => `<li>${esc(warning)}</li>`).join('')}</ul>`;
}

function opsUnavailable(summary: AdminOpsSummary): string {
  if (summary.unavailable.length === 0) return '<p class="muted">No unavailable metrics.</p>';
  return `<ul>${summary.unavailable.map((item) => `<li>${esc(item)}</li>`).join('')}</ul>`;
}

function opsSection(state: AppState): string {
  const summary = state.adminOpsSummary;
  if (!summary) {
    return `
      <section class="card card--wide admin-panel">
        <div class="run__head">
          <h2>Ops / Monitoring</h2>
          ${btn('Refresh', 'admin-ops-refresh', {}, { variant: 'ghost' })}
        </div>
        <p class="muted">No ops summary loaded.</p>
      </section>`;
  }

  return `
    <section class="card card--wide admin-panel">
      <div class="run__head">
        <h2>Ops / Monitoring</h2>
        ${btn('Refresh', 'admin-ops-refresh', {}, { variant: 'ghost' })}
      </div>
      <div class="admin-ops-grid">
        ${row('Environment', `<code>${esc(summary.environment.mode)}</code>`)}
        ${row('Verifier modes', `${verifierPill(summary.environment.verifierModes.cashout)} ${verifierPill(summary.environment.verifierModes.spinner)}`)}
        ${row('DB ready', summary.database.ready ? pill('ready', 'ok') : pill('down', 'down'))}
        ${row('Migration', summary.database.migration.available ? `<code>${esc(summary.database.migration.latestMigration ?? 'available')}</code>` : esc(summary.database.migration.reason ?? 'unavailable'))}
        ${row('Uptime', `<code>${esc(summary.app.uptimeSeconds)}s</code>`)}
        ${row('Reward pause', summary.ops.rewardRunsPaused ? pill('paused', 'warn') : pill('open', 'ok'))}
        ${row('Reward runs', `<code>${esc(summary.rewardRuns.active)}</code> active · <code>${esc(summary.rewardRuns.startedLast24h)}</code> started / 24h`)}
        ${row('Terminals / 24h', `<code>${esc(summary.rewardRuns.terminalLast24h.extracted)}</code> extracted · <code>${esc(summary.rewardRuns.terminalLast24h.died)}</code> died · <code>${esc(summary.rewardRuns.terminalLast24h.abandoned)}</code> abandoned · <code>${esc(summary.rewardRuns.terminalLast24h.interrupted)}</code> interrupted`)}
        ${row('Orders', `<code>${esc(summary.orders.marketplace.active)}</code> item · <code>${esc(summary.orders.cosmeticMarketplace.active)}</code> cosmetic · <code>${esc(summary.orders.cashout.active)}</code> cashout · <code>${esc(summary.orders.spinnerPaid.pending)}</code> spinner pending`)}
        ${row('Solana verifications', summary.solanaVerification.available ? `<code>${esc(summary.solanaVerification.totalLast24h ?? 0)}</code> / 24h` : esc(summary.solanaVerification.unavailableReason ?? 'unavailable'))}
      </div>
      <div class="admin-abuse-grid">
        <div class="admin-detail">
          <h3>Warnings</h3>
          ${opsWarnings(summary)}
          <h3>Unavailable</h3>
          ${opsUnavailable(summary)}
        </div>
        <div class="admin-detail">
          <h3>Details</h3>
          <pre class="admin-pre">${esc(jsonText(summary))}</pre>
        </div>
      </div>
    </section>`;
}

function replaySummary(result: RunReplayResult | null): string {
  if (!result) return '<p class="muted">No replay result yet.</p>';
  const title = result.ok ? 'Replay OK' : 'Replay mismatch';
  const bannerClass = result.ok ? 'banner--notice' : 'banner--error';
  const reasons = result.mismatchReasons.length > 0
    ? `<ul>${result.mismatchReasons.map((reason) => `<li><code>${esc(reason)}</code></li>`).join('')}</ul>`
    : '<p class="muted">No mismatch reasons.</p>';
  const settlement = result.settlement
    ? `<pre class="admin-pre">${esc(jsonText(result.settlement))}</pre>`
    : '';
  return `
    <div class="banner ${bannerClass}"><strong>${esc(title)}</strong> <code>${esc(result.runId)}</code></div>
    <div class="admin-replay-grid">
      ${row('Stored', `<code>${esc(result.storedStatus)}</code>`)}
      ${row('Replay', `<code>${esc(result.replayStatus)}</code>`)}
      ${row('Actions', `<code>${esc(result.actionCount)}</code>`)}
      ${row('Terminal floor', `<code>${esc(result.terminalFloor ?? '—')}</code>`)}
      ${row('Extract floor', `<code>${esc(result.extractFloor ?? '—')}</code>`)}
    </div>
    <h3>Mismatch reasons</h3>
    ${reasons}
    ${settlement}`;
}

function replaySection(state: AppState): string {
  return `
    <section class="card card--wide admin-panel">
      <div class="run__head">
        <h2>Run Audit / Replay</h2>
      </div>
      <div class="admin-token-row">
        <input class="market-price admin-token-input" type="text" data-admin-replay-run placeholder="run id" aria-label="Run id for replay" />
        ${btn('Replay', 'admin-replay-run', {}, { variant: 'primary' })}
      </div>
      ${replaySummary(state.adminReplayResult)}
    </section>`;
}

export function renderAdmin(state: AppState): string {
  if (!state.adminToken) return lockedState();
  const config = state.adminConfig;
  return `
    <div class="grid admin-grid">
      <section class="card card--wide admin-head">
        <div class="run__head">
          <h2>Admin config</h2>
          <div class="btn-row">
            ${btn('Refresh', 'admin-refresh', {}, { variant: 'ghost' })}
            ${btn('Clear token', 'admin-token-clear', {}, { variant: 'ghost' })}
            ${btn('Back to Waystation', 'goto-waystation', {}, { variant: 'ghost' })}
          </div>
        </div>
        <p class="banner banner--notice">${esc(WARNING)}</p>
        ${row('Admin token', `<code>${esc(tokenPreview(state.adminToken))}</code>`)}
      </section>
      ${opsSection(state)}
      ${abuseSection(state)}
      ${replaySection(state)}
      ${config ? configList(config) : '<section class="card card--wide"><h2>Config</h2><p class="muted">No config loaded.</p></section>'}
    </div>`;
}
